import { useState } from 'react';
import { X, Copy, Download, Check, Twitter, Facebook, Send, Instagram, Linkedin, Link as LinkIcon } from 'lucide-react';
import { BACKEND_URL } from '@/lib/api';

/**
 * Share modal for any certificate. Uses public endpoints so no auth needed for viewers.
 * Supports: X/Twitter, Facebook, Telegram, WhatsApp, LinkedIn, native Web Share, copy link,
 * download image (for Instagram/TikTok manual upload).
 */
export default function ShareCertModal({ cert, onClose }) {
  const [copied, setCopied] = useState(false);
  const [imgCopied, setImgCopied] = useState(false);
  if (!cert) return null;

  const publicUrl = `${window.location.origin}/cert/${cert.certificate_id}`;
  const imageUrl = `${BACKEND_URL}/api/public/certificates/${cert.certificate_id}/image`;
  const pdfUrl = `${BACKEND_URL}/api/public/certificates/${cert.certificate_id}/download`;

  const titleByType = {
    payout: `I just earned a performance reward with Traders Spring 💚`,
    phase_1_passed: `Phase 1 cleared with Traders Spring 💚`,
    phase_2_passed: `Phase 2 cleared with Traders Spring — next stop: Funded phase 💚`,
    funded: `I'm now a Funded Trader with Traders Spring 🌱`,
  };
  const text = titleByType[cert.cert_type] || `My Traders Spring certificate 🌱`;
  const hashtags = 'TradersSpringGroup,SyntheticIndices,EvaluationProgram';

  const links = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(publicUrl)}&hashtags=${hashtags}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}&quote=${encodeURIComponent(text)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(publicUrl)}&text=${encodeURIComponent(text)}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + publicUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`,
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  const copyImage = async () => {
    try {
      const r = await fetch(imageUrl);
      const blob = await r.blob();
      if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        setImgCopied(true); setTimeout(() => setImgCopied(false), 1500);
      } else {
        // fallback: download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `TSG-${cert.certificate_id}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch {}
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'Traders Spring Group', text, url: publicUrl }); } catch {}
    } else copy();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in" onClick={onClose} data-testid="share-modal">
      <div className="card-tech rounded-xl max-w-xl w-full p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="share-close"><X size={18} /></button>
        <div className="label-sm">Share certificate</div>
        <h2 className="font-display text-2xl mt-1">Let the world know.</h2>
        <p className="text-xs text-zinc-500 mt-1 break-all">{publicUrl}</p>

        <div className="mt-5 rounded-lg overflow-hidden border border-white/10">
          <img src={imageUrl} alt="Certificate" className="w-full block" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-5">
          <SocialBtn name="X" icon={Twitter} href={links.twitter} testId="share-twitter" />
          <SocialBtn name="Facebook" icon={Facebook} href={links.facebook} testId="share-facebook" />
          <SocialBtn name="Telegram" icon={Send} href={links.telegram} testId="share-telegram" />
          <SocialBtn name="WhatsApp" icon={Send} href={links.whatsapp} testId="share-whatsapp" />
          <SocialBtn name="LinkedIn" icon={Linkedin} href={links.linkedin} testId="share-linkedin" />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={copyImage} className="btn-ghost btn-sm justify-center" data-testid="share-copy-image">
            <Instagram size={14} className="inline mr-1" /> {imgCopied ? 'Image copied' : 'Copy image (IG / TikTok)'}
          </button>
          <button onClick={copy} className="btn-ghost btn-sm justify-center" data-testid="share-copy-link">
            {copied ? <Check size={14} className="inline mr-1 text-[#00ff66]" /> : <LinkIcon size={14} className="inline mr-1" />}
            {copied ? 'Link copied' : 'Copy link'}
          </button>
        </div>

        <div className="mt-3 flex gap-2">
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-ghost btn-sm flex-1 justify-center" data-testid="share-download-pdf">
            <Download size={14} className="inline mr-1" /> Download PDF
          </a>
          <button onClick={nativeShare} className="btn-primary btn-sm flex-1 justify-center" data-testid="share-native">
            Share via device
          </button>
        </div>

        <div className="text-[11px] text-zinc-500 mt-4 text-center">
          Instagram and TikTok don't support direct web sharing — copy the image and upload it from their apps.
        </div>
      </div>
    </div>
  );
}

function SocialBtn({ name, icon: Icon, href, testId }) {
  return (
    <a href={href} target="_blank" rel="noreferrer"
       className="card-tech rounded-md p-3 text-center hover:border-[#00ff66]/50 transition flex flex-col items-center gap-1"
       data-testid={testId}>
      <Icon size={16} className="text-[#00ff66]" />
      <span className="text-xs">{name}</span>
    </a>
  );
}
